<!-- Talent Pool Container with Scrolling -->
<!-- `#scrollContainer` template ref is consumed by the V3 infiniteScroll
     directive below. The previous setup used `[infiniteScrollContainer]="'.talent-pool-container'"`
     with `[fromRoot]="true"`, which resolves via `document.querySelector`. Because
     both this component and `app-talent-pool` use the same class name
     `.talent-pool-container`, on a tab switch back from Talent Pool the old
     Talent Pool container was still briefly in the DOM during this component's
     directive initialization — `querySelector` returned that stale element, the
     scroll listener attached to a soon-to-be-detached node, and `(scrolled)`
     stopped firing. Passing the element ref directly eliminates the DOM query. -->
<div #scrollContainer id="talent-pool-container" class="talent-pool-container">

  <!-- ════════════════════════════════════════════════════════════════
       V3 DESIGN — Marketplace Candidates using candidate-card-table-view
       ════════════════════════════════════════════════════════════════ -->
  <ng-container *ngIf="designVersion === 'v3'">
    <!-- AI Smart Search banner — matches the marketplace identity. Shown
         regardless of state so users always see what's powering the list. -->
    <div class="jc-v3-ai-banner">
      <div class="jc-v3-ai-banner-icon">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/><path d="M11 8v6M8 11h6"/></svg>
      </div>
      <div class="jc-v3-ai-banner-body">
        <div class="jc-v3-ai-banner-title">
          Results powered by Smart Search
          <span class="jc-v3-ai-chip">
            <svg width="8" height="8" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/></svg>
            AI
          </span>
        </div>
        <div class="jc-v3-ai-banner-sub">Freelance recruiters and marketplace candidates are surfaced based on job fit, location, and network proximity.</div>
      </div>
    </div>

    <!-- Summary stat cards — mirrors the Marketplace wireframe
         (gigsumo_job_center_v2(16).html #tab-marketplace). All three are
         wired to the findTopSuggestedCandidates response: Available =
         totalCount, Top Match = maxScore, Via Recruiters =
         freelanceRecruiterCount. -->
    <div class="jc-v3-stat-grid jc-v3-stat-grid-3">
      <div class="jc-v3-stat-card acc-purple">
        <div class="jc-v3-s-label">Marketplace</div>
        <div class="jc-v3-s-value">{{ marketplaceTotalCount || 0 }}</div>
        <div class="jc-v3-s-sub">Available candidates</div>
      </div>
      <div class="jc-v3-stat-card acc-green">
        <div class="jc-v3-s-label">Top Match</div>
        <div class="jc-v3-s-value">{{ topMatchScore || 0 }}%</div>
        <div class="jc-v3-s-sub">Highest match score</div>
      </div>
      <div class="jc-v3-stat-card acc-amber">
        <div class="jc-v3-s-label">Via Recruiters</div>
        <div class="jc-v3-s-value">{{ viaRecruitersCount || 0 }}</div>
        <div class="jc-v3-s-sub">Freelance network</div>
      </div>
    </div>

    <!-- Infinite scroll listens on `.talent-pool-container` — this component's
         own root (see scss: `height: 100%; overflow: auto`), which is the
         element that actually receives scroll events. Listening on the outer
         `.jc-v3-entity-details-body` does not work because this inner container
         claims the overflow first, so wheel events never bubble out and
         `(scrolled)` never fires. Same fix as talent-pool. -->
    <!-- Job-level disable: blocked / non-active job greys the whole
         Marketplace Candidates listing and blocks pointer events.
         Mirrors job-applicants.component.html:62. -->
    <div infiniteScroll
      [infiniteScrollDistance]="5"
      [infiniteScrollThrottle]="200"
      [infiniteScrollContainer]="scrollContainer"
        [infiniteScrollDisabled]="!responseReceived || loadAPIcall || stopscrollFlag"
      [fromRoot]="false"
      [alwaysCallback]="true"
      [ngClass]="{'disabled': !jobDetails?.availability || jobDetails?.status !== 'ACTIVE'}"
      (scrolled)="onScrollDown()" id="entity-container">

      <app-candidate-card-table-view
        [designVersion]="'v3'"
        [pageName]="'suggested-candidates'"
        [response]="candidatesList"
        [loadAPIcall]="loadAPIcall"
        [stopscrollFlag]="stopscrollFlag"
        [responseReceived]="responseReceived"
        [jobDetails]="jobDetails"
        [userId]="userId"
        [showTimeline]="false"
        (onCandidateClick)="route($event)"
        (onViewProfile)="open($event)"
        (onDownloadResume)="downloadResume($event)"
        (onSendMessage)="sendMessage($event)"
        (onOpenDocuments)="openDocuments($event)"
        (onShareProfile)="shareProfile($event)"
        (onScheduleInterview)="scheduleInterview($event)"
        (onAddNotes)="addNotes($event)"
        (onStatusUpdate)="updateStatus($event.status, $event.candidate)"
        (showDetails)="showDetails($event, DetailsPopup)"
        (inviteClicked)="invite($event)">
      </app-candidate-card-table-view>

      <!-- Scroll-pagination skeleton — parity with talent-pool V3 (talent-pool.component.html:103-106).
           `loadAPIcall` flips true in onScrollDown() before the next page fetch,
           back to false in the API next/error handlers, so this only shows while
           a subsequent-page request is in flight. -->
      <div class="col-12" *ngIf="loadAPIcall && candidatesList?.length > 0">
        <app-common-skeleton [countforView]="2" [NameOftheScreen]="'jobcardlarge'"></app-common-skeleton>
      </div>
<!-- <div style="color:red">DEBUG: length={{candidatesList?.length}} responseReceived={{responseReceived}} loadAPIcall={{loadAPIcall}}</div> -->
      <!-- Empty state — split on `responseReceived` so first-load shows
           "AI is analyzing" copy (matches `noResultMessage` getter in TS),
           and post-response shows the "no matches" state. Previously V3
           always rendered the "No Marketplace Candidates Found" state
           regardless of phase, so users never saw the AI-processing
           progress text. -->
      <!-- <div *ngIf="!loadAPIcall && candidatesList?.length === 0 && !responseReceived" class="jc-v3-empty-state">
        <svg width="200" height="180" viewBox="0 0 200 180" fill="none" xmlns="http://www.w3.org/2000/svg">
          <circle cx="100" cy="90" r="80" fill="#F5F3FF"/>
          <circle cx="100" cy="90" r="40" fill="#EDE9FE" stroke="#7C3AED" stroke-width="1.8"/>
          <path d="M100 70 v25 l15 10" stroke="#7C3AED" stroke-width="2.5" stroke-linecap="round" fill="none"/>
          <circle cx="148" cy="58" r="14" fill="#C4B5FD"/>
          <polygon points="148,51 151,57 157,57 152,61 154,67 148,63 142,67 144,61 139,57 145,57" fill="#ffffff"/>
          <circle cx="50" cy="52" r="3" fill="#A78BFA"/>
          <circle cx="155" cy="128" r="2" fill="#C4B5FD"/>
        </svg>
        <h3>AI is analyzing candidates</h3>
        <p>{{ noResultMessage }}</p>
      </div> -->
    </div>
    <!-- AI-processing + empty states live OUTSIDE the infiniteScroll div
         above: that div carries the job-level `disabled` ngClass (grey +
         pointer-events none for non-ACTIVE jobs, e.g. Draft), which would
         wash these states out. Mirrors Talent Pool placement. -->
    <!-- AI-processing state — covers the initial findTopSuggestedCandidates
         call AND its retry attempts (3 × 25s in suggestionsWithRetry):
         loadAPIcall stays true for the whole window. After the last failed
         retry the error handler flips loadAPIcall false → the "No
         Marketplace Candidates Found" empty state below takes over. -->
    <div *ngIf="loadAPIcall && candidatesList?.length === 0" class="jc-v3-empty-state">
      <svg width="200" height="180" viewBox="0 0 200 180" fill="none" xmlns="http://www.w3.org/2000/svg">
        <circle cx="100" cy="90" r="80" fill="#F5F3FF"/>
        <circle cx="100" cy="90" r="40" fill="#EDE9FE" stroke="#7C3AED" stroke-width="1.8"/>
        <path d="M100 70 v25 l15 10" stroke="#7C3AED" stroke-width="2.5" stroke-linecap="round" fill="none"/>
        <circle cx="148" cy="58" r="14" fill="#C4B5FD"/>
        <polygon points="148,51 151,57 157,57 152,61 154,67 148,63 142,67 144,61 139,57 145,57" fill="#ffffff"/>
        <circle cx="50" cy="52" r="3" fill="#A78BFA"/>
        <circle cx="155" cy="128" r="2" fill="#C4B5FD"/>
      </svg>
      <h3>AI is analyzing</h3>
      <p>Our AI is analyzing this job and finding the best matching candidates for you...</p>
    </div>
    <div *ngIf="!loadAPIcall && candidatesList?.length === 0" class="jc-v3-empty-state">
      <svg width="200" height="180" viewBox="0 0 200 180" fill="none" xmlns="http://www.w3.org/2000/svg">
        <circle cx="100" cy="90" r="80" fill="#F5F3FF"/>
        <rect x="58" y="50" width="84" height="72" rx="8" fill="#ffffff" stroke="#7C3AED" stroke-width="1.8"/>
        <rect x="68" y="64" width="32" height="4" rx="2" fill="#DDD6FE"/>
        <rect x="68" y="73" width="44" height="4" rx="2" fill="#EDE9FE"/>
        <rect x="68" y="82" width="36" height="4" rx="2" fill="#EDE9FE"/>
        <circle cx="120" cy="74" r="14" fill="#EDE9FE" stroke="#7C3AED" stroke-width="1.5"/>
        <circle cx="120" cy="72" r="6" fill="#ffffff" stroke="#7C3AED" stroke-width="1.3"/>
        <path d="M109 90 c0-8 5-13 11-13h0c6 0 11 5 11 13" fill="#EDE9FE" stroke="#7C3AED" stroke-width="1.5" stroke-linecap="round"/>
        <circle cx="148" cy="130" r="16" fill="#7C3AED" opacity="0.10"/>
        <circle cx="148" cy="130" r="11" fill="#ffffff" stroke="#7C3AED" stroke-width="2"/>
        <line x1="144" y1="130" x2="152" y2="130" stroke="#7C3AED" stroke-width="2" stroke-linecap="round"/>
        <line x1="148" y1="126" x2="148" y2="134" stroke="#7C3AED" stroke-width="2" stroke-linecap="round"/>
        <circle cx="50" cy="52" r="3" fill="#A78BFA"/>
        <circle cx="155" cy="56" r="2" fill="#C4B5FD"/>
      </svg>
      <h3>No Marketplace Candidates Yet</h3>
      <p *ngIf="!isDraftJob">AI-matched candidates for this job will appear here.</p>
      <p *ngIf="isDraftJob">AI cannot match candidates for drafted jobs.</p>
    </div>
  </ng-container>

  <!-- ════════════════════════════════════════════════════════════════
       V2 DESIGN (existing) — old table layout
       ════════════════════════════════════════════════════════════════ -->
  <ng-container *ngIf="designVersion !== 'v3'">

  <!-- <div class="header-row">
    <div class="search-filter-section">
      <div class="search-wrapper" [class.expanded]="searchExpanded">
        <div class="search-input-container">
          <div class="search-icon-wrapper">
            <ng-icon name="tablerSearch"></ng-icon>
          </div>
          <input
            #searchInput
            id="talent_pool_search"
            type="text"
            class="search-input-field"
            placeholder="Search candidates..."
            [(ngModel)]="searchText"
            (keyup)="getListOfCandidates()"
            (blur)="onSearchBlur()"
            (focus)="onSearchFocus()">
        </div>
        <button class="icon-btn search-toggle-btn" (click)="toggleSearch()" title="Search">
          <ng-icon name="tablerSearch"></ng-icon>
        </button>
      </div>
      <button id="talent_pool_filter_button" class="icon-btn filter-btn" (click)="openFilterSlider()" title="Filter">
        <ng-icon name="tablerFilter"></ng-icon>
        <span *ngIf="activeFiltersCount > 0" class="filter-count">{{activeFiltersCount}}</span>
      </button> 
    </div> 
  </div>  -->

  <!-- <app-candidate-card-table-view
    [pageName]="'suggested-candidates'"
    [response]="candidatesList"
    [loadAPIcall]="loadAPIcall"
    [stopscrollFlag]="stopscrollFlag"
    [responseReceived]="responseReceived"
    [jobDetails]="jobDetails"
    [userId]="userId"
    [showTimeline]="false"
    (onCandidateClick)="route($event)"
    (onViewProfile)="open($event)"
    (onDownloadResume)="downloadResume($event)"
    (onSendMessage)="sendMessage($event)"
    (onOpenDocuments)="openDocuments($event)"
    (onShareProfile)="shareProfile($event)"
    (onScheduleInterview)="scheduleInterview($event)"
    (onAddNotes)="addNotes($event)"
    (onStatusUpdate)="updateStatus($event.status, $event.candidate)"
    (showDetails)="showDetails($event,DetailsPopup)"
    (inviteClicked)="invite($event)">
  </app-candidate-card-table-view> -->

  <!-- Table Structure using candidate-card with row-design view -->
  <!-- Job-level disable: blocked / non-active job greys the V2
       Marketplace Candidates listing and blocks pointer events. -->
  <div class="suggested-candidates-table-wrapper"
    [style.height]="'calc(100vh - 197px)'"
    infiniteScroll [infiniteScrollDistance]="5" [scrollWindow]="false"
    [infiniteScrollThrottle]="200" [alwaysCallback]="true"
    [ngClass]="{'disabled': !jobDetails?.availability || jobDetails?.status !== 'ACTIVE'}"
    (scrolled)="onScrollDown()">
    <div class="suggested-candidates-table-container hpanel">

      <!-- Table Header -->
      <div class="suggested-candidates-table-header panel-body">
        <div class="header-cell rank-col">Rank</div>
        <div class="header-cell candidate-col">Candidate</div>
        <div class="header-cell location-col">Title & Location</div>
        <div class="header-cell skills-col">Skills</div>
        <div class="header-cell match-col">Match %</div>
        <div class="header-cell engagement-col">Engagement</div>
        <div class="header-cell actions-col">Quick Actions</div>
      </div>

      <!-- Loading Skeleton -->
      <div class="col-12" *ngIf="loadAPIcall && candidatesList?.length==0">
        <app-common-skeleton [countforView]="4" [NameOftheScreen]="'jobcardlarge'"></app-common-skeleton>
      </div>

      <!-- Candidate Rows using candidate-card with row-design view -->
      <ng-container *ngIf="candidatesList.length > 0; else noResult">
        <div class="suggested-candidates-table-body">
          <ng-container *ngFor="let item of candidatesList; let i = index">
            <app-candidate-card
              [candidateval]="setRankAndShowSkills(item, i + 1)"
              [viewSize]="'row-design'"
              [pageName]="'suggested-candidates'"
              [appliedJobDetails]="jobDetails"
              [userId]="userId"
              [showStatus]="false"
              [showTimeline]="false">
            </app-candidate-card>
          </ng-container>
        </div>

        <!-- Loading Skeleton - During Scroll/Pagination -->
        <div class="col-12" *ngIf="loadAPIcall && candidatesList.length > 0">
          <app-common-skeleton [countforView]="2" [NameOftheScreen]="'jobcardlarge'"></app-common-skeleton>
        </div>

        <!-- Bottom Reached Message -->
        <div *ngIf="stopscrollFlag && candidatesList.length > 0" class="caught_up">
          <div class="col-md-12">
            <ng-icon name="tablerCircleCheck"></ng-icon>
          </div>
          <div class="col-md-12 fig">You've reached the bottom of the page!</div>
        </div>
      </ng-container>

      <!-- No Result Template -->
      <ng-template #noResult>
        <div class="wert" *ngIf="!loadAPIcall || responseReceived">
          <div class="col-md-12 mg-bt-9">
            <ng-icon [name]="noResultSvgType"></ng-icon>
          </div>
          <div class="col-md-12 fig">{{noResultMessage}}</div>
        </div>
      </ng-template>

    </div>
  </div>

  </ng-container><!-- end V2 -->

  <ng-template #DetailsPopup let-modal>
    <div class="match-details-modal">
      <div class="modal-header-enhanced">
        <div class="header-content">
          <div class="score-badge-container">
            <div class="score-badge">
              <span class="score-value">{{candidateScore}}</span>
              <span class="score-label">Match Score</span>
            </div>
          </div>
          <div class="header-text">
            <h3 class="modal-title">Candidate Match Analysis</h3>
            <p class="modal-subtitle">Detailed breakdown of compatibility factors</p>
          </div>
        </div>
        <button type="button" class="close-btn-modern" aria-label="Close" (click)="closeModal()">
          <ng-icon name="tablerX"></ng-icon>
        </button>
      </div>

      <!-- Modal Body with Enhanced Sections -->
      <div class="modal-body-enhanced">
        <div *ngFor="let r of reasons; let i = index" class="reason-card">

          <!-- Overall Reason Section -->
          <div class="reason-summary-section">
            <div class="section-icon reason-icon">
              <ng-icon name="tablerInfoCircle"></ng-icon>
            </div>
            <div class="section-content">
              <h4 class="section-title">Match Summary</h4>
              <p class="reason-summary-text">{{ r.reason }}</p>
            </div>
          </div>

          <!-- Strengths Section with Green Theme -->
          <div class="strengths-section">
            <div class="section-header">
              <div class="section-icon strengths-icon">
                <!-- <ng-icon name="tablerCircleCheck"></ng-icon> -->
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                    <path fill-rule="evenodd"
                      d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                      clip-rule="evenodd" />
                      </svg>
              </div>
              <h4 class="section-title strengths-title">
                Strengths
                <span class="count-badge strengths-badge">{{ r.strengths?.length || 0 }}</span>
              </h4>
            </div>
            <div class="section-body">
              <div class="strength-item" *ngFor="let s of r.strengths; let idx = index">
                <div class="item-bullet strengths-bullet">
                  <!-- <ng-icon name="tablerCheck"></ng-icon> -->
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                    <path fill-rule="evenodd"
                      d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                      clip-rule="evenodd" />
                  </svg>
                </div>
                <span class="item-text">{{ s }}</span>
              </div>
              <div *ngIf="!r.strengths || r.strengths.length === 0" class="empty-state">
                <ng-icon name="tablerInfoCircle"></ng-icon>
                <span>No specific strengths identified</span>
              </div>
            </div>
          </div>

          <!-- Weaknesses Section with Red Theme -->
          <div class="weaknesses-section">
            <div class="section-header">
              <div class="section-icon weaknesses-icon">
                <!-- <ng-icon name="tablerAlertTriangle"></ng-icon> -->
                <ng-icon name="tablerAlertTriangle"></ng-icon>
              </div>
              <h4 class="section-title weaknesses-title">
                Areas for Improvement
                <span class="count-badge weaknesses-badge">{{ r.weaknesses?.length || 0 }}</span>
              </h4>
            </div>
            <div class="section-body">
              <div class="weakness-item" *ngFor="let w of r.weaknesses; let idx = index">
                <div class="item-bullet weaknesses-bullet">
                  <!-- <ng-icon name="tablerCircleMinus"></ng-icon> -->
                  <ng-icon name="tablerAlertCircle"></ng-icon>
                </div>
                <span class="item-text">{{ w }}</span>
              </div>
              <div *ngIf="!r.weaknesses || r.weaknesses.length === 0" class="empty-state">
                <ng-icon name="tablerThumbUp"></ng-icon>
                <span>No significant gaps identified</span>
              </div>
            </div>
          </div>

          
        </div>

        <!-- Empty State if No Reasons -->
        <div *ngIf="!reasons || reasons.length === 0" class="no-data-state">
          <ng-icon name="tablerSearch"></ng-icon>
          <p>No match analysis available for this candidate.</p>
        </div>
      </div>

      <!-- Enhanced Footer -->
      <div class="modal-footer-enhanced">
        <button class="btn-close-modern" (click)="closeModal()">
          Close
        </button>
      </div>
    </div>
  </ng-template>

</div>

<ng-template #jobsupplytemplate>
  <app-new-job [focus]="focus" [screenName]="'invite-job-listing'" [filterData]="candidateData"></app-new-job>
</ng-template>